import { LockClosedIcon } from '@heroicons/react/16/solid'
import { useQuery } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { QUERY_KEYS } from 'constants/constants'
import trackEvents from '../../constants/track-events'
import { useNotificationContext, usePartnerContext } from '../../context'
import { useTalentStorage } from '../../features/pricing/hooks'
import { usePricingDialog } from '../../features/pricing/pricing-dialog-context'
import { query } from '../../graphql'
import { notifyBugsnag } from '../../helpers'
import { trackEvent } from '../../helpers/analytics'
import { buildCompareCandidates } from '../../helpers/compare-candidates'
import { useTalentsQuery } from '../../hooks/use-talents-query'
import ActivityIndicator from '../activity-indicator'
import Alert from '../alert'
import Button from '../tailwind/Button'
import InfoCallout from '../tailwind/InfoCallout'
import Modal from '../tailwind/Modal'
import Panel from '../tailwind/Panel'
import SectionHeading from '../tailwind/SectionHeading'
import CompareSpaceCombobox from './space-radar-chart/compare-space-combobox'
import RadarChartComplete from './space-radar-chart/radar-chart-complete'
import RadarChartPreview from './space-radar-chart/radar-chart-preview'

// Fuer einen Vergleich braucht das Diagramm eine Mindestzahl gemeinsamer
// Dimensionen, sonst rendert es ohne Achsen oder als entartete Linie. Gleiche
// Untergrenze wie in der Teamanalyse.
const MIN_COMPARE_DIMENSIONS = 3

const queryAnswers = async ({ owner }) => {
  const response = await query({
    query: 'helperControl',
    variables: { action: 'listAnswersWithAivyOutput', user_id: owner }
  })

  try {
    return JSON.parse(response).body.listAnswersWithAivyOutput.res.reduce(
      (object, item) => ({ ...object, [item.exam_id]: item }),
      {}
    )
  } catch (err) {
    throw new Error(
      `${err.message} - listAnswersWithAivyOutput: ${JSON.stringify(response)}`
    )
  }
}

const SpaceRadarChart = ({ space: originSpace, career }) => {
  const { t } = useTranslation()
  const { error } = useNotificationContext()
  const { partner } = usePartnerContext()

  const { data, status: talentsStatus } = useTalentsQuery()
  // Talentspeicher. Die Antworten laden hier per owner, an der Maske der
  // Talentliste vorbei, deshalb prüft der Radar isLocked selbst. Solange der
  // Speicher pending ist, gilt jedes Talent als gesperrt (kein Aufblitzen).
  // Außerhalb von enforced ist nichts gesperrt und alles bleibt wie heute.
  const { isLocked, pending: storagePending } = useTalentStorage({
    fetch: 'auto'
  })
  const { openLimitDialog } = usePricingDialog()

  const [compareSpace, setCompareSpace] = useState({})
  const [displayModal, setDisplayModal] = useState(false)

  const originLocked = isLocked(originSpace.id)
  const compareLocked = !!compareSpace.id && isLocked(compareSpace.id)

  const { locked, lockedCount, ...compareCandidates } = useMemo(
    () =>
      buildCompareCandidates({
        spaces: data?.spaces,
        originSpaceId: originSpace.id,
        isLocked
      }),
    [data?.spaces, originSpace.id, isLocked]
  )

  // Quelle pro Zeile, gleiches Muster wie die Spalte "Quelle" der Talentliste.
  // space.assessment_id ist career_id, 'TALENTPOOL' oder 'ATTRACT'.
  const careersMap = useMemo(
    () =>
      new Map([
        ...(data?.careers || []).map((item) => [item.id, item]),
        ['TALENTPOOL', { title: t('talents.talentpool_title') }],
        ['ATTRACT', { title: 'Attract' }]
      ]),
    [data?.careers, t]
  )

  const {
    data: originAnswers,
    status: originStatus,
    error: originError
  } = useQuery({
    queryKey: QUERY_KEYS.answersWithAivyOutputOrigin(originSpace.owner),
    queryFn: () => queryAnswers({ owner: originSpace.owner }),
    enabled: !originLocked
  })

  const {
    data: compareAnswers,
    status: compareStatus,
    error: compareError
  } = useQuery({
    queryKey: QUERY_KEYS.answersWithAivyOutputCompare(compareSpace.owner),
    queryFn: () => queryAnswers({ owner: compareSpace.owner }),
    enabled: !!compareSpace.owner && !compareLocked
  })

  // Seiteneffekt im Effekt statt im Render, sonst warnt React beim Setzen des
  // Notification-Context waehrend des Renderns einer anderen Komponente.
  useEffect(() => {
    if (originStatus !== 'error' && compareStatus !== 'error') return

    error()
    notifyBugsnag(originError || compareError)
  }, [originStatus, compareStatus, originError, compareError, error])

  // Check if Cultural Fit/Radar Chart is visible based on partner settings
  if (!partner.isDashboardComponentVisible?.culturalFit) {
    return null
  }

  // Gesperrtes Talent zeigt keinen Radar. Ein Cache aus der Zeit vor der
  // Sperre käme trotz enabled false zurück, deshalb hier ausdrücklich.
  if (originLocked && !storagePending) {
    return null
  }

  if (originStatus === 'error' || compareStatus === 'error') {
    return null
  }

  const isOriginLoading = storagePending || originStatus === 'pending'
  const canShowOrigin = !originLocked && originStatus === 'success'
  const canShowCompare =
    canShowOrigin && !compareLocked && compareStatus === 'success'

  const chartFooter = (
    <p className='mt-8 text-center text-sm italic text-gray-700'>
      {t('radar_chart.show_hide_hint')}
    </p>
  )

  return (
    <>
      <Panel className='overflow-visible'>
        <SectionHeading
          id='radar-chart-title'
          title={t('space.radar_chart.title')}
          description={t('space.radar_chart.description')}
        />
        <div className='border-t border-gray-300 pt-5' />
        {isOriginLoading ? (
          <ActivityIndicator className='flex justify-center py-8' />
        ) : (
          <>
            <div className='flex justify-center'>
              <RadarChartPreview
                space={{ ...originSpace, answers: originAnswers }}
              />
            </div>
            <div
              style={{ maxWidth: 256 }}
              className='relative mx-auto mt-4 flex flex-col gap-y-1'
            >
              <Button.PrimaryLG
                text={t('space.radar_chart.show_talent_action')}
                onClick={() => {
                  trackEvent(trackEvents.CLICK_VIEW_RADARCHART_TALENT)
                  setDisplayModal(true)
                }}
              />

              <CompareSpaceCombobox
                {...compareCandidates}
                careersMap={careersMap}
                status={talentsStatus}
                onSelect={(space) => {
                  setCompareSpace(space)
                  setDisplayModal(true)
                }}
              />

              {lockedCount > 0 && (
                <p
                  data-compare-locked-hint=''
                  className='mt-1.5 text-center text-xs text-gray-500'
                >
                  <LockClosedIcon
                    aria-hidden='true'
                    className='mr-1 inline-block size-3.5 align-text-bottom text-gray-400'
                  />
                  {t('pricing.talent_locked.compare_locked_hint', {
                    count: lockedCount
                  })}{' '}
                  <button
                    type='button'
                    aria-haspopup='dialog'
                    onClick={() =>
                      openLimitDialog('talentStorage', {
                        source: 'talent_radar_compare',
                        action: 'info'
                      })
                    }
                    className='rounded font-semibold text-buttonSecondaryBlue underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primaryBlue'
                  >
                    {t('pricing.banner.details_action')}
                  </button>
                </p>
              )}
            </div>
          </>
        )}
      </Panel>
      <Modal
        size='4xl'
        open={displayModal}
        setOpen={() => {
          setDisplayModal(false)
          setTimeout(() => setCompareSpace({}), 1000 * 0.8)
        }}
      >
        <div className='mt-8' />
        {!compareSpace.owner && canShowOrigin && (
          <RadarChartComplete
            spaces={[{ ...originSpace, answers: originAnswers }]}
            career={career?.app_settings ? career : null}
            footer={chartFooter}
          />
        )}

        {compareSpace.owner &&
          !compareLocked &&
          compareStatus === 'pending' && (
            <ActivityIndicator className='flex items-center justify-center p-64' />
          )}

        {canShowCompare && (
          <RadarChartComplete
            spaces={[
              { ...originSpace, answers: originAnswers },
              { ...compareSpace, answers: compareAnswers }
            ]}
            minDimensions={MIN_COMPARE_DIMENSIONS}
            footer={
              <>
                {chartFooter}
                <InfoCallout className='mt-2'>
                  <strong>{t('radar_chart.good_to_know_title')}</strong>{' '}
                  {t('radar_chart.compare_missing_dimensions_hint')}
                </InfoCallout>
              </>
            }
            emptyState={
              <Alert
                type='warning'
                className='my-8'
                title={t(
                  'space.radar_chart.compare_no_shared_dimensions_title'
                )}
                description={t(
                  'space.radar_chart.compare_no_shared_dimensions_description',
                  {
                    origin: originSpace.identification,
                    compare: compareSpace.identification
                  }
                )}
              />
            }
          />
        )}
      </Modal>
    </>
  )
}

SpaceRadarChart.propTypes = {
  space: PropTypes.object.isRequired,
  career: PropTypes.object
}

export default SpaceRadarChart
